import React, { useEffect, useState } from 'react';
import { Button, Input, Modal, Form, Popover, Row } from 'antd';
import TableComponent from '../../../components/common/TableComponent';
import { useTranslation } from 'react-i18next';
import { PencilFill, Plus, ThreeDotsVertical, Trash, XSquare } from 'react-bootstrap-icons';
import { ActionMenu } from '../../../components/common/ActionMenu';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import ConfirmModelComponent from '../../../components/model/ConfirmationModelComponent';

import {
  getEstablishmentTypeList,
  createEstablishmentType,
  updateEstablishmentType,
  deleteEstablishmentType,
} from '../getData';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';

const EstablishmentTypeTable = () => {
  const { t } = useTranslation(['administration']);
  const { isOnline } = useIsOnlineContext();
  const [dataList, setDataList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isShowDeleteConfirmation, setIsShowDeleteConfirmation] = useState<boolean>(false);
  const [isShowEditConfirmation, setIsShowEditConfirmation] = useState<boolean>(false);
  const [editValues, setEditValues] = useState<any>(null);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [form] = Form.useForm();
  const { successNotification, errorNotification } = useNotificationContext();
  const [paginationInfo, setPaginationInfo] = useState<{ page: number; limit: number }>({
    page: 1,
    limit: 10,
  });

  const columns = [
    {
      title: t('establishmentTypeId'),
      dataIndex: 'id',
      key: 'id',
      width: 220,
    },
    {
      title: t('englishName', 'English Name'),
      dataIndex: 'nameEn',
      key: 'nameEn',
      width: 225,
    },
    {
      title: t('spanishName', 'Spanish Name'),
      dataIndex: 'nameEs',
      key: 'nameEs',
      width: 225,
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('actions'),
      key: 'actions',
      align: 'center',
      width: 120,
      render: (_: any, record: any) => {
        const menuItems = [
          {
            text: t('edit'),
            icon: <PencilFill size={16} className="text-primary-blue-icon" />,
            click: () => handleEdit(record),
          },
          {
            text: t('delete'),
            icon: <Trash size={16} className="text-primary-blue-icon" />,
            click: () => handleDelete(record.id),
          },
        ];

        return (
          <Popover
            content={<ActionMenu menuItems={menuItems} />}
            className="cursor-pointer"
            placement="bottom"
            trigger="hover"
            overlayStyle={{ zIndex: 800 }}
          >
            <Row justify="center">
              <ThreeDotsVertical size={15} />
            </Row>
          </Popover>
        );
      },
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await getEstablishmentTypeList();
      setDataList(Array.isArray(data) ? data : []);
    } catch (error) {
      errorNotification(t('failedToLoadEstablishmentTypes') as string);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (record: any) => {
    setEditingItem(record);
    form.setFieldsValue({ nameEn: record.nameEn, nameEs: record.nameEs, description: record.description });
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    setDeleteItemId(id);
    setIsShowDeleteConfirmation(true);
  };

  const confirmDelete = async () => {
    if (!deleteItemId) return;
    try {
      await deleteEstablishmentType(deleteItemId);
      await referenceDataSyncService.forceSyncEntity('establishmentTypes');
      successNotification(
        t('establishmentTypeDeletedSuccessfully') as string
      );
      fetchData();
      setIsShowDeleteConfirmation(false);
      setDeleteItemId(null);
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message;
      if (errorMsg) {
        errorNotification(t(errorMsg, errorMsg) as string);
      } else {
        errorNotification(t('failedToDeleteEstablishmentType') as string);
      }
    }
  };
  const cancelDelete = () => {
    setIsShowDeleteConfirmation(false);
    setDeleteItemId(null);
  };

  const deleteConfirmationHeaderContent = () => {
    return (
      <label className="text-primary-font-gray my-6 px-12 text-center text-xl font-bold">
        {t('deleteTypeConfirmation')}
      </label>
    );
  };

  const executeModalSubmit = async (values: any) => {
    try {
      if (editingItem) {
        await updateEstablishmentType(editingItem.id, values);
        successNotification(
          t('establishmentTypeUpdatedSuccessfully') as string
        );
      } else {
        await createEstablishmentType(values);
        successNotification(
          t('establishmentTypeCreatedSuccessfully') as string
        );
      }
      await referenceDataSyncService.forceSyncEntity('establishmentTypes');
      setIsModalVisible(false);
      form.resetFields();
      setEditingItem(null);
      fetchData();
    } catch (error: any) {
      const errorMsg = error?.response?.data?.message || error?.message;
      if (errorMsg) {
        errorNotification(t(errorMsg, errorMsg) as string);
      } else {
        errorNotification(t('failedToSaveEstablishmentType') as string);
      }
    }
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingItem && editingItem.hasEstablishments) {
        setEditValues(values);
        setIsShowEditConfirmation(true);
      } else {
        await executeModalSubmit(values);
      }
    } catch (error) {
      // Form validation failed, ignore
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingItem(null);
  };

  const onPaginationChanged = (page: number, limit: number) => {
    setPaginationInfo({ page, limit });
  };

  return (
    <div className="w-full">
      <div className="mb-4 flex justify-end">
        <PrimarySquareBtn
          handleOnClick={() => setIsModalVisible(true)}
          disabled={!isOnline}
        >
          <div className="flex flex-row items-center gap-2">
            <Plus className="h-6 w-6 text-white" />{' '}
            {t('addEstablishmentType')}
          </div>
        </PrimarySquareBtn>
      </div>

      <TableComponent
        tableData={dataList}
        columns={columns}
        pageSize={paginationInfo.limit}
        currentPage={paginationInfo.page}
        totalItems={dataList.length}
        onPaginationChanged={onPaginationChanged}
        scroll={{ x: 'max-content' }}
        showHeader
        loading={loading}
      />

      <Modal
        title={
          editingItem
            ? t('editEstablishmentType')
            : t('addEstablishmentType')
        }
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        okText={editingItem ? t('update') : t('create')}
        cancelText={t('cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="nameEn"
            label={t('englishName', 'English Name')}
            rules={[{ required: true, message: t('pleaseInputEnglishName', 'Please input English name') }]}
          >
            <Input placeholder={t('establishmentTypeEnglishNamePlaceholder', 'Establishment Type English Name')} />
          </Form.Item>
          <Form.Item
            name="nameEs"
            label={t('spanishName', 'Spanish Name')}
            rules={[{ required: true, message: t('pleaseInputSpanishName', 'Please input Spanish name') }]}
          >
            <Input placeholder={t('establishmentTypeSpanishNamePlaceholder', 'Establishment Type Spanish Name')} />
          </Form.Item>
          <Form.Item name="description" label={t('description')}>
            <Input.TextArea
              placeholder={t('establishmentTypeDescriptionPlaceholder')}
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmModelComponent
        isOpen={isShowDeleteConfirmation}
        headerContent={deleteConfirmationHeaderContent()}
        content={t('deleteTypeConfirmationMessage')}
        onClickedCancel={cancelDelete}
        footerAction={{
          confimation: {
            label: t('deleteNow'),
            onClick: confirmDelete,
          },
          cancel: {
            label: t('cancel'),
            onClick: cancelDelete,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
      />

      <ConfirmModelComponent
        isOpen={isShowEditConfirmation}
        headerContent={<label className="text-primary-font-gray my-6 px-12 text-center text-xl font-bold">{t('warning')}</label>}
        content={t('editEstablishmentTypeWarning')}
        onClickedCancel={() => setIsShowEditConfirmation(false)}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: () => {
              setIsShowEditConfirmation(false);
              executeModalSubmit(editValues);
            },
          },
          cancel: {
            label: t('no'),
            onClick: () => setIsShowEditConfirmation(false),
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
      />
    </div>
  );
};

export default EstablishmentTypeTable;
